/**
 * WEB-2552: «в хранилище всё ещё лежит ровно то, что ушло в запрос
 * сохранения?».
 *
 * Пока запрос сохранения в пути, автор может продолжать править. Ответ
 * сервера раньше безусловно ставил «всё сохранено» (`isDirty: false`) —
 * правка, сделанная за это время, пропадала молча: индикатор «Сохранено»,
 * вкладка закрывается без вопроса, автосохранение молчит, при уходе
 * сохранения нет. Теперь успех сохранения гасит «не сохранено», только если
 * эта проверка отвечает «да»; иначе признак остаётся, и автосохранение
 * дорабатывает следующей попыткой.
 *
 * Сравнение по ссылкам: каждая правка редактора кладёт новый объект имени
 * (строка), содержимого, оформления или правил. Совпала ссылка — после
 * отправки правок не было (отмена правки, вернувшая прежний объект, тоже
 * честно считается «то же самое»).
 *
 * Правила в аргумент обычного сохранения не входят — санк читает их из
 * хранилища сам и кладёт то, что отправил, меткой `sentLogic` в ответ
 * (`thunks/save.ts`). Действие без метки бывает только собранное руками в
 * тестах — тогда сверка идёт по снимку правил из аргумента, а без него
 * правила не сверяются.
 *
 * Обработчики `extraReducers` получают черновик immer, а у черновика вложенные
 * объекты — обёртки-посредники, не равные по ссылке исходным; поэтому сверка
 * идёт по `current(state)`, где нетронутые ветки — те самые объекты.
 */
import { current, isDraft } from '@reduxjs/toolkit';
import type { Rule } from '@/logic-engine';
import type { EditorState } from './state';
import type { EditorScreen } from './types/screen';
import type { EditorSettings } from './types/settings';

/** Метка ответа санка сохранения: какие правила ушли на сервер. */
export interface SentDraftMeta {
  sentLogic: Rule[];
}

/** То, что ушло в запрос сохранения (аргумент санка). */
export interface SentDraftArg {
  name: string;
  content: EditorScreen[];
  settings?: EditorSettings;
  logic?: Rule[];
}

export function storeHoldsSentDraft(
  state: Pick<EditorState, 'name' | 'content' | 'settings' | 'logic'>,
  arg: SentDraftArg,
  meta: Partial<SentDraftMeta> | undefined,
): boolean {
  const now = isDraft(state) ? current(state) : state;
  const sentLogic = meta?.sentLogic ?? arg.logic;
  return (
    now.name === arg.name &&
    now.content === arg.content &&
    now.settings === arg.settings &&
    (sentLogic === undefined || now.logic === sentLogic)
  );
}
